import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Form, Select, Tag, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { getCredentials } from '../../Credentials/CredentialsServices';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';

const { Text } = Typography;

const getCredentialName = (credential) => (
    credential.name || credential.first_name || credential.email || credential.credential_user_id || 'Unnamed'
);
const getCredentialDescription = (credential) => (
    credential.email || credential.credential_user_id || credential.last_name || ''
);
const getCredentialChannelTypeLabel = (channelType, t) => {
    if (!channelType) return '';
    if (channelType === 'facebook_page') {
        return t('facebook', { defaultValue: 'Facebook' });
    }

    return t(channelType, { defaultValue: channelType });
};
const getCredentialSearchText = (credential) => [
    credential.name,
    credential.first_name,
    credential.last_name,
    credential.email,
    credential.credential_user_id,
    credential.channel_type,
].filter(Boolean).join(' ').toLowerCase();

const renderCredentialOption = (credential, t) => {
    const name = getCredentialName(credential);
    const description = getCredentialDescription(credential);
    const channelTypeLabel = getCredentialChannelTypeLabel(credential.channel_type, t);

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Avatar
                size={34}
                src={credential.picture_url}
                icon={<UserOutlined />}
                style={{ flexShrink: 0 }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <Text strong style={{ lineHeight: 1.2, minWidth: 0 }} ellipsis>
                        {name}
                    </Text>
                    {channelTypeLabel && (
                        <Tag color="blue" style={{ margin: 0, flexShrink: 0 }}>
                            {channelTypeLabel}
                        </Tag>
                    )}
                </div>
                {description && (
                    <Text type="secondary" style={{ display: 'block', fontSize: 12, lineHeight: 1.25 }} ellipsis>
                        {description}
                    </Text>
                )}
            </div>
        </div>
    );
};

const ChannelCredentialEditModal = ({ channel, onCancel, onUpdate, loading = false }) => {
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [form] = Form.useForm();
    const [credentials, setCredentials] = useState([]);
    const [credentialsLoading, setCredentialsLoading] = useState(false);

    const currentCredentialId = channel.credential_id ?? channel.credential?.id ?? null;

    useEffect(() => {
        form.setFieldsValue({ credential_id: currentCredentialId });
    }, [currentCredentialId, form]);

    useEffect(() => {
        const controller = new AbortController();

        const fetchCredentials = async () => {
            setCredentialsLoading(true);
            try {
                const response = await getCredentials({
                    page: 1,
                    size: 100,
                    signal: controller.signal,
                });
                setCredentials(response.data || []);
            } catch (error) {
                if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
                    handleError(error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setCredentialsLoading(false);
                }
            }
        };

        fetchCredentials();

        return () => controller.abort();
    }, [handleError]);

    const credentialOptions = useMemo(() => {
        const options = credentials.map((credential) => ({
            value: credential.id,
            label: renderCredentialOption(credential, t),
            displayLabel: getCredentialName(credential),
            searchText: getCredentialSearchText(credential),
        }));

        if (channel.credential?.id && !options.some((option) => option.value === channel.credential.id)) {
            options.unshift({
                value: channel.credential.id,
                label: renderCredentialOption(channel.credential, t),
                displayLabel: getCredentialName(channel.credential),
                searchText: getCredentialSearchText(channel.credential),
            });
        }

        return options;
    }, [credentials, channel.credential, t]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const success = await onUpdate(channel.id, {
                credential_id: values.credential_id,
            }, form);

            if (success) {
                onCancel();
            }
        } catch (error) {
            if (error.errorFields) return;
            handleError(error, form);
        }
    };

    return (
        <ResponsiveModal
            title={t('edit_credential')}
            open
            onCancel={onCancel}
            desktopWidth={420}
            footer={(
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={onCancel}>
                        {t('cancel')}
                    </Button>
                    <Button type="primary" onClick={handleSubmit} loading={loading}>
                        {t('save')}
                    </Button>
                </div>
            )}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label={t('credential')}
                    name="credential_id"
                    rules={[{ required: true, message: t('please_select_credential') }]}
                >
                    <Select
                        placeholder={t('select_credential')}
                        loading={credentialsLoading}
                        options={credentialOptions}
                        optionLabelProp="displayLabel"
                        showSearch
                        filterOption={(input, option) => (
                            option?.searchText?.includes(input.toLowerCase().trim())
                        )}
                    />
                </Form.Item>
            </Form>
        </ResponsiveModal>
    );
};

export default ChannelCredentialEditModal;
